/**
 * 導覽播放統計——船機回報了什麼、播了幾次。
 *
 * ## 為什麼數字要換算
 *
 * 船機送上來的 `zh_guide_plays` 是**開機以來的累計值**（業主 2026-08-19 確認），
 * 不是「這段期間播了幾次」。直接把每次回報的數字加起來會把同一批播放重複算上
 * 幾百遍，而算出來的東西看起來完全正常——所以這一頁刻意把「累計值」與「增量」
 * 分成兩個區塊呈現，不讓人有機會把兩者當成同一種數字。
 *
 * ## 歸零次數也是資訊
 *
 * 計數器變小代表船機重開或韌體重裝過。`resets` 因此同時是「船機在這段期間重開
 * 了幾次」——那個數字異常大時要查的是船機穩定性，不是導覽有多熱門。
 */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../components/Button';
import {
  fetchGuidePlays,
  listDisplayTelemetry,
  type PlayGranularity,
  type TelemetryRecord,
} from '../lib/api-client';

/** 預設區間：近 30 天（含今日）。 */
function defaultRange(): { from: string; to: string } {
  const today = new Date();
  const from = new Date(today);
  from.setDate(from.getDate() - 29);
  const iso = (d: Date): string => d.toISOString().slice(0, 10);
  return { from: iso(from), to: iso(today) };
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleString('zh-TW', { hour12: false, timeZone: 'Asia/Taipei' });
}

/** 增量的 null 是「無從得知」而不是 0——顯示上必須看得出差別。 */
function fmtDelta(v: number | null): JSX.Element {
  if (v === null) return <span style={{ color: '#9ca3af' }}>—</span>;
  return <span style={{ fontWeight: 600 }}>+{v}</span>;
}

export function DisplayTelemetryPage(): JSX.Element {
  const initial = defaultRange();
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [granularity, setGranularity] = useState<PlayGranularity>('day');
  const [range, setRange] = useState({ from: initial.from, to: initial.to, granularity: 'day' });

  const stats = useQuery({
    queryKey: ['display-telemetry', 'guide-plays', range],
    queryFn: () =>
      fetchGuidePlays({
        from: range.from,
        to: range.to,
        granularity: range.granularity as PlayGranularity,
      }),
  });

  const [showRaw, setShowRaw] = useState(false);
  const raw = useQuery({
    queryKey: ['display-telemetry', 'list'],
    queryFn: () => listDisplayTelemetry({ limit: 50 }),
    enabled: showRaw,
  });

  const report = stats.data;
  const maxPlays = Math.max(1, ...(report?.rows ?? []).map((r) => r.total_plays));

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">導覽播放統計</h1>
      </div>

      <div
        style={{
          background: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: 6,
          padding: 12,
          margin: '12px 0',
          fontSize: 13,
          maxWidth: 1000,
        }}
      >
        船機靠港時隨 <code>POST /api/cms/update</code> 回報導覽播放次數、GPS 與系統狀態 （規格書
        §15）。回報的是<strong>開機以來的累計值</strong>，下方的播放次數已換算成
        <strong>該期間的增量</strong>；計數器變小（船機重開或韌體重裝）時以歸零處理，並計入
        <code>重開次數</code>。
      </div>

      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          margin: '12px 0',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
          起日
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            style={{ padding: 8 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
          迄日（含當日）
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            style={{ padding: 8 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
          粒度
          <select
            value={granularity}
            onChange={(e) => setGranularity(e.target.value as PlayGranularity)}
            style={{ padding: 8 }}
          >
            <option value="day">日</option>
            <option value="week">週</option>
            <option value="month">月</option>
          </select>
        </label>
        <Button variant="primary" onClick={() => setRange({ from, to, granularity })}>
          {stats.isFetching ? '查詢中…' : '查詢'}
        </Button>
      </div>

      {stats.error && <div className="error-banner">{(stats.error as Error).message}</div>}

      {report && (
        <>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', margin: '12px 0' }}>
            <Stat label="中文播放" value={`${report.total.zh_plays} 次`} />
            <Stat label="英文播放" value={`${report.total.en_plays} 次`} />
            <Stat label="合計" value={`${report.total.total_plays} 次`} />
            <Stat label="回報筆數" value={`${report.total.reports} 筆`} />
            <Stat
              label="計數器歸零"
              value={`${report.total.resets} 次`}
              warn={report.total.resets > 0}
            />
          </div>

          <div style={{ fontSize: 12, color: '#9ca3af', margin: '0 0 12px' }}>
            日界以 <code>{report.timezone}</code> 切。查詢會多取一筆起日之前的回報當差分基準，
            所以區間第一天的數字不會短少。
          </div>

          {report.latest ? (
            <div className="table-card" style={{ maxWidth: 1000, marginBottom: 16 }}>
              <div style={{ padding: 12, fontSize: 13 }}>
                <strong>船上最後回報</strong>（{fmtTime(report.latest.received_at)}）
                <div style={{ marginTop: 6, color: '#374151' }}>
                  版本 <code>{report.latest.client_version ?? '—'}</code>
                  {'　'}累計中文 <strong>{report.latest.zh_guide_plays ?? '—'}</strong>
                  {'　'}累計英文 <strong>{report.latest.en_guide_plays ?? '—'}</strong>
                  {'　'}狀態{' '}
                  <span style={{ color: report.latest.status === 0 ? '#059669' : '#dc2626' }}>
                    {report.latest.status === 0
                      ? '正常'
                      : report.latest.status === null
                        ? '—'
                        : `異常（${report.latest.status}）`}
                  </span>
                </div>
                <div style={{ marginTop: 4, color: '#6b7280', fontSize: 12 }}>
                  GPS / Heading：<code>{report.latest.gps_heading ?? '—'}</code>
                  {'　'}（此處為<strong>累計值</strong>原樣，非增量）
                </div>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: '#6b7280', margin: '12px 0' }}>
              船機從未回報過播放次數。<code>/api/cms/update</code> 有被呼叫但沒帶
              <code>zh_guide_plays</code> / <code>en_guide_plays</code> 時也會是這樣。
            </div>
          )}

          {report.rows.length === 0 ? (
            <div style={{ fontSize: 13, color: '#6b7280', margin: '12px 0' }}>
              這個區間沒有任何帶播放數的回報。
            </div>
          ) : (
            <div className="table-card" style={{ maxWidth: 1000, overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>期間</th>
                    <th style={{ textAlign: 'right' }}>中文</th>
                    <th style={{ textAlign: 'right' }}>英文</th>
                    <th style={{ textAlign: 'right' }}>合計</th>
                    <th style={{ width: 220 }}>分布</th>
                    <th style={{ textAlign: 'right' }}>回報</th>
                    <th style={{ textAlign: 'right' }}>重開</th>
                  </tr>
                </thead>
                <tbody>
                  {report.rows.map((r) => (
                    <tr key={r.period}>
                      <td>
                        <code>{r.period}</code>
                      </td>
                      <td style={{ textAlign: 'right' }}>{r.zh_plays}</td>
                      <td style={{ textAlign: 'right' }}>{r.en_plays}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{r.total_plays}</td>
                      <td>
                        <Bar zh={r.zh_plays} en={r.en_plays} max={maxPlays} />
                      </td>
                      <td style={{ textAlign: 'right', color: '#6b7280' }}>{r.reports}</td>
                      <td
                        style={{
                          textAlign: 'right',
                          color: r.resets > 0 ? '#d97706' : '#6b7280',
                        }}
                      >
                        {r.resets}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <div style={{ margin: '20px 0 8px' }}>
        <Button variant="secondary" onClick={() => setShowRaw((v) => !v)}>
          {showRaw ? '收起原始回報' : '檢視原始回報'}
        </Button>
        <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 8 }}>
          統計是推導出來的；數字看起來不對時，這裡是原始證據。
        </span>
      </div>

      {showRaw && (
        <>
          {raw.error && <div className="error-banner">{(raw.error as Error).message}</div>}
          {raw.data && (
            <div className="table-card" style={{ overflowX: 'auto' }}>
              <div style={{ padding: '8px 12px', fontSize: 12, color: '#6b7280' }}>
                最近 {raw.data.rows.length} 筆（全表 {raw.data.total} 筆）。Δ 為相對上一次回報的
                增量，<code>—</code> 代表無從得知（序列第一筆或該次沒帶播放數）。
              </div>
              <table>
                <thead>
                  <tr>
                    <th>收到時間</th>
                    <th>回報版本</th>
                    <th style={{ textAlign: 'right' }}>中文累計</th>
                    <th style={{ textAlign: 'right' }}>Δ</th>
                    <th style={{ textAlign: 'right' }}>英文累計</th>
                    <th style={{ textAlign: 'right' }}>Δ</th>
                    <th>狀態</th>
                    <th>GPS / Heading</th>
                    <th>來源</th>
                  </tr>
                </thead>
                <tbody>
                  {raw.data.rows.map((r: TelemetryRecord) => (
                    <tr key={r.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtTime(r.received_at)}</td>
                      <td>
                        <code>{r.client_version ?? '—'}</code>
                      </td>
                      <td style={{ textAlign: 'right' }}>{r.zh_guide_plays ?? '—'}</td>
                      <td style={{ textAlign: 'right' }}>{fmtDelta(r.zh_delta)}</td>
                      <td style={{ textAlign: 'right' }}>{r.en_guide_plays ?? '—'}</td>
                      <td style={{ textAlign: 'right' }}>{fmtDelta(r.en_delta)}</td>
                      <td>
                        {r.counter_reset && (
                          <span style={{ color: '#d97706', fontSize: 12 }}>↺ 歸零 </span>
                        )}
                        {r.status === null ? '—' : r.status === 0 ? '正常' : `異常（${r.status}）`}
                      </td>
                      <td style={{ fontSize: 12, color: '#6b7280' }}>{r.gps_heading ?? '—'}</td>
                      <td
                        style={{
                          fontSize: 11,
                          color: '#9ca3af',
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={r.source_hint ?? ''}
                      >
                        {r.source_hint ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  warn,
}: {
  label: string;
  value: string;
  warn?: boolean;
}): JSX.Element {
  return (
    <div>
      <div style={{ fontSize: 12, color: '#6b7280' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 600, color: warn ? '#d97706' : undefined }}>
        {value}
      </div>
    </div>
  );
}

/** 中英文佔比的橫條——看的是語系分布，不是絕對值，故以區間最大值為滿格。 */
function Bar({ zh, en, max }: { zh: number; en: number; max: number }): JSX.Element {
  const pct = (v: number): string => `${(v / max) * 100}%`;
  return (
    <div style={{ display: 'flex', height: 12, background: '#f3f4f6', borderRadius: 2 }}>
      <div style={{ width: pct(zh), background: '#2563eb' }} title={`中文 ${zh}`} />
      <div style={{ width: pct(en), background: '#60a5fa' }} title={`英文 ${en}`} />
    </div>
  );
}
